import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Material } from '@babylonjs/core/Materials/material';
import type { Scene } from '@babylonjs/core/scene';

/**
 * Torchlight: real point lights from a zone's torches, braziers and lanterns (2026-10-01).
 *
 * ── Why a pool ──────────────────────────────────────────────────────────────
 *
 * `engine.ts` records why the town's windows glow instead of lighting anything: a standard
 * material takes four lights, and the sun and the sky are two of them, so a light per source is
 * not expressible. A zone has dozens of sources and the camera sees a handful. So there are
 * `TORCH_POOL` lights, and each frame they go to the sources nearest what the camera looks at.
 * `TORCH_MAX_LIGHTS` raises every material's limit to the sun, the sky and the pool.
 *
 * ── Brighter, not moodier ───────────────────────────────────────────────────
 *
 * The owner's standing complaint is that levels are too dark and monsters hard to see. These
 * lights only add: nothing is dimmed to make them read. The flicker is ±`FLICKER` around full
 * strength, never down to dark.
 */

/** A light source in the world: where its flame is and what colour it burns. */
export interface LightSpot {
  x: number;
  y: number;
  z: number;
  colour: readonly [number, number, number];
}

/** How many point lights exist at once. Chosen: four is the most a room shows. */
export const TORCH_POOL = 4;
/** The sun, the sky, and the pool. */
export const TORCH_MAX_LIGHTS = 2 + TORCH_POOL;
/** How far a torch lights, world units. Chosen by looking: two tiles and more of a room. */
export const TORCH_RANGE = 18;
/** Peak intensity. Chosen by looking. */
export const TORCH_INTENSITY = 2.6;
/** The flicker's depth, as a fraction of the intensity: a flame, not a strobe. */
export const FLICKER = 0.12;

/** The `n` spots nearest `(x, z)`, nearest first. Pure. */
export function nearestSpots(spots: readonly LightSpot[], x: number, z: number, n: number): LightSpot[] {
  return spots
    .map((s) => ({ s, d: (s.x - x) ** 2 + (s.z - z) ** 2 }))
    .sort((a, b) => a.d - b.d)
    .slice(0, n)
    .map((e) => e.s);
}

/**
 * A flame's brightness at time `t` (seconds) as a multiple of full strength, in `[1 - FLICKER, 1]`.
 * Three incommensurate sines, phased per spot, so neighbouring torches do not pulse together. Pure.
 */
export function flicker(t: number, phase: number): number {
  const n = (Math.sin(t * 7.3 + phase) + Math.sin(t * 13.1 + phase * 2.7) * 0.6 + Math.sin(t * 3.7 + phase * 5.1) * 0.4) / 2;
  return 1 - FLICKER * (0.5 + 0.5 * n);
}

export class TorchLights {
  private readonly lights: PointLight[] = [];
  private spots: LightSpot[] = [];
  private readonly frame: Observer<Scene> | null;
  private readonly materialHook: Observer<Material> | null;

  constructor(
    private readonly scene: Scene,
    /** Where to light around — the camera's target. */
    private readonly focus: () => { x: number; z: number },
  ) {
    for (let i = 0; i < TORCH_POOL; i++) {
      const light = new PointLight(`torchlight-${i}`, Vector3.Zero(), scene);
      light.range = TORCH_RANGE;
      light.intensity = 0;
      // A flame lights; it does not put a hot spot on stone.
      light.specular = Color3.Black();
      // Always enabled, idle at zero: switching a light on or off changes how many lights every
      // material's shader is compiled for, and recompiles the scene.
      this.lights.push(light);
    }
    const raise = (m: Material) => {
      const limited = m as Material & { maxSimultaneousLights?: number };
      if (typeof limited.maxSimultaneousLights === 'number' && limited.maxSimultaneousLights < TORCH_MAX_LIGHTS) {
        limited.maxSimultaneousLights = TORCH_MAX_LIGHTS;
      }
    };
    for (const m of scene.materials) raise(m);
    // Babylon notifies this a timer tick after the constructor (`Scene.addMaterial`), so the
    // material's own default of four is already set when it is raised.
    this.materialHook = scene.onNewMaterialAddedObservable.add(raise);
    this.frame = scene.onBeforeRenderObservable.add(() => this.update(performance.now() / 1000));
  }

  /** The zone's light sources. Empty turns every light off. */
  setSpots(spots: readonly LightSpot[]): void {
    this.spots = [...spots];
    this.update(performance.now() / 1000);
  }

  /** Assigns the pool to the nearest spots and flickers them. Public for tests. */
  update(t: number): void {
    const at = this.focus();
    const near = nearestSpots(this.spots, at.x, at.z, TORCH_POOL);
    this.lights.forEach((light, i) => {
      const spot = near[i];
      if (!spot) {
        light.intensity = 0;
        return;
      }
      light.position.set(spot.x, spot.y, spot.z);
      light.diffuse.set(spot.colour[0], spot.colour[1], spot.colour[2]);
      light.intensity = TORCH_INTENSITY * flicker(t, spot.x * 1.7 + spot.z * 0.9);
    });
  }

  /** The lights themselves, for tests. */
  get pool(): readonly PointLight[] {
    return this.lights;
  }

  dispose(): void {
    if (this.frame) this.scene.onBeforeRenderObservable.remove(this.frame);
    if (this.materialHook) this.scene.onNewMaterialAddedObservable.remove(this.materialHook);
    for (const l of this.lights) l.dispose();
  }
}
